import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/updateMemberPhoneNumberAndPostalAddress';
export const REQUEST_PHONE_NUMBER_ACTION_NAME = `${ACTION_NAME_PREFIX}/requestPhoneNumber`;
export const REQUEST_POSTAL_ADDRESS_ACTION_NAME = `${ACTION_NAME_PREFIX}/requestPostalAddress`;
export const RESPONSE_PHONE_NUMBER_ACTION_NAME = `${ACTION_NAME_PREFIX}/responsePhoneNumber`;
export const RESPONSE_POSTAL_ADDRESS_ACTION_NAME = `${ACTION_NAME_PREFIX}/responsePostalAddress`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Updates member postal address for a member.
 * @param {object} logger
 * @returns {Promise<object>}
 */
export const UpdateMemberPhoneNumberAndPostalAddress = (
    streetAddress,
    suite,
    city,
    stateOrProvince,
    country,
    postalOrZipCode,
    phoneNumber
) => {
    return async (dispatch, getState, { logger }) => {
        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const memberPostalAddress = getState().MemberData.member.postalAddress;
        const features = getState().Features.data;
        const transactionId = logger.generateGuid();

        let addressId;

        if (features['postalAddress']) {
            dispatch({ type: REQUEST_POSTAL_ADDRESS_ACTION_NAME });

            const updatedAddress = { streetAddress, suite, city, stateOrProvince, country, postalOrZipCode };

            logger.info(`${ACTION_NAME_PREFIX}: Updating member postal address.`, { correlation: { transactionId } });

            if (memberPostalAddress?.addressId) {
                const updatePostalAddressMutation = await fetch(`${BFF_URL}/api/v2`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'gb-authorization': jwt,
                        'mli-session-correlation-id': sessionCorrelationId,
                        'mli-correlation-id': transactionId,
                        'mli-channel': 'web',
                        'gb-client-name': GBCORE_CLIENT_NAME,
                        'gb-client-version': GBCORE_CLIENT_VERSION,
                        'Accept-Language': language,
                    },
                    body: JSON.stringify({
                        operationName: 'UpdateMemberPostalAddressMutation',
                        variables: {
                            postalAddress: {
                                addressId: memberPostalAddress.addressId,
                                status: memberPostalAddress.status,
                                type: memberPostalAddress.type,
                                streetAddress,
                                suite,
                                city,
                                stateOrProvince,
                                country,
                                postalOrZipCode,
                            },
                        },
                        query: `
                        mutation UpdateMemberPostalAddressMutation($postalAddress: PostalAddressInput) {
                            updateMemberPostalAddress(postalAddress: $postalAddress){
                                addressId
                            }
                        }
                    `,
                    }),
                });

                const updatePostalAddressMutationResponse = await updatePostalAddressMutation.json();

                if (updatePostalAddressMutationResponse.errors) {
                    const { message, code } = updatePostalAddressMutationResponse.errors[0];
                    logger.error(
                        `${ACTION_NAME_PREFIX}: Failure when updating member postal address. Code: ${code} Reason: ${message}`,
                        { correlation: { transactionId } }
                    );
                    return dispatch({
                        type: ERROR_ACTION_NAME,
                        code,
                        message,
                    });
                }
                addressId = updatePostalAddressMutationResponse.data.updateMemberPostalAddress.addressId;
                dispatch({
                    type: RESPONSE_POSTAL_ADDRESS_ACTION_NAME,
                    addressId,
                    updatedAddress,
                });
            } else {
                const createPostalAddressMutation = await fetch(`${BFF_URL}/api/v2`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'gb-authorization': jwt,
                        'mli-session-correlation-id': sessionCorrelationId,
                        'mli-correlation-id': transactionId,
                        'mli-channel': 'web',
                        'gb-client-name': GBCORE_CLIENT_NAME,
                        'gb-client-version': GBCORE_CLIENT_VERSION,
                        'Accept-Language': language,
                    },
                    body: JSON.stringify({
                        operationName: 'CreateMemberPostalAddressMutation',
                        variables: {
                            postalAddress: {
                                type: 'CANADAPOST',
                                streetAddress,
                                suite,
                                city,
                                stateOrProvince,
                                country,
                                postalOrZipCode,
                            },
                        },
                        query: `
                        mutation CreateMemberPostalAddressMutation($postalAddress: PostalAddressInput) {
                            createMemberPostalAddress(postalAddress: $postalAddress){
                                addressId
                            }
                        }
                    `,
                    }),
                });

                const createPostalAddressMutationResponse = await createPostalAddressMutation.json();

                if (createPostalAddressMutationResponse.errors) {
                    const { message, code } = createPostalAddressMutationResponse.errors[0];
                    logger.error(
                        `${ACTION_NAME_PREFIX}: Failure when creating member postal address. Code: ${code} Reason: ${message}`,
                        { correlation: { transactionId } }
                    );
                    return dispatch({
                        type: ERROR_ACTION_NAME,
                        code,
                        message,
                    });
                }
                addressId = createPostalAddressMutationResponse.data.createMemberPostalAddress.addressId;
                dispatch({
                    type: RESPONSE_POSTAL_ADDRESS_ACTION_NAME,
                    addressId,
                    updatedAddress,
                });
            }
        }

        // If there is an error in postal address then do not submit change for phone number
        // GB Core update phone number mutation handles both MC phone number update and create
        if (features['phoneNumber'] && !getState().Errors.postalAddressUpdate.hasError) {
            dispatch({ type: REQUEST_PHONE_NUMBER_ACTION_NAME });

            const updatedPhoneNumber = { phoneNumber };

            logger.info(`${ACTION_NAME_PREFIX}: Updating member phone number.`, { correlation: { transactionId } });

            const updatePhoneNumberMutation = await fetch(`${BFF_URL}/api/v2`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'gb-authorization': jwt,
                    'mli-session-correlation-id': sessionCorrelationId,
                    'mli-correlation-id': transactionId,
                    'mli-channel': 'web',
                    'gb-client-name': GBCORE_CLIENT_NAME,
                    'gb-client-version': GBCORE_CLIENT_VERSION,
                    'Accept-Language': language,
                },
                body: JSON.stringify({
                    operationName: 'UpdateMemberPhoneNumberMutation',
                    variables: {
                        phoneNumber,
                    },
                    query: `
                    mutation UpdateMemberPhoneNumberMutation($phoneNumber: String) {
                        updateMemberPhoneNumberByGroup(phoneNumber: $phoneNumber)
                    }
                `,
                }),
            });

            const updatePhoneNumberMutationResponse = await updatePhoneNumberMutation.json();

            if (updatePhoneNumberMutationResponse.errors) {
                const { message, code } = updatePhoneNumberMutationResponse.errors[0];
                logger.error(
                    `${ACTION_NAME_PREFIX}: Failure when updating member phone number. Code: ${code} Reason: ${message}`,
                    { correlation: { transactionId } }
                );
                return dispatch({
                    type: ERROR_ACTION_NAME,
                    code,
                    message,
                });
            }
            dispatch({ type: RESPONSE_PHONE_NUMBER_ACTION_NAME, updatedPhoneNumber });
        }

        return { success: true };
    };
};

export const requestPhoneNumberReducer = produce((state) => {
    state.MemberData.isSavingPostalAddressInformation = true;
});

export const requestPostalAddressReducer = produce((state) => {
    state.MemberData.isSavingPostalAddressInformation = true;
});

export const responsePhoneNumberReducer = produce((state, action) => {
    state.MemberData.isSavingPostalAddressInformation = false;
    state.MemberData.member.postalAddress.phoneNumber = action.updatedPhoneNumber.phoneNumber;
});

export const responsePostalAddressReducer = produce((state, action) => {
    // Only set address saving flag to false when completed postal address if user has no phone number entitlement
    state.MemberData.isSavingPostalAddressInformation = state.Features.data.phoneNumber;
    state.MemberData.member.postalAddress = {
        ...state.MemberData.member.postalAddress,
        ...action.updatedAddress,
    };
    state.MemberData.member.postalAddress.addressId = action.addressId;
});

/**
 * Fired off when the request encounters an error.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.MemberData.isSavingPostalAddressInformation = false;
    state.Errors.postalAddressUpdate.hasError = true;
    state.Errors.postalAddressUpdate.code = action.code;
    state.Errors.postalAddressUpdate.message = action.message;
});

registerReducer(REQUEST_PHONE_NUMBER_ACTION_NAME, requestPhoneNumberReducer);
registerReducer(REQUEST_POSTAL_ADDRESS_ACTION_NAME, requestPostalAddressReducer);
registerReducer(RESPONSE_PHONE_NUMBER_ACTION_NAME, responsePhoneNumberReducer);
registerReducer(RESPONSE_POSTAL_ADDRESS_ACTION_NAME, responsePostalAddressReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
